import { Component, OnInit, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ConfigurationService } from 'src/app/services/configuration.service';
import { WallSide } from 'src/app/enums/wallSide.enum';
import { WallFinish } from 'src/app/models/wallFinish.model';
import { FinishTypes } from 'src/app/enums/finishTypes.enum';
import { WallFinishUpdate } from 'src/app/models/wallFinishUpdate.model';
import { ThreeDService } from 'src/app/services/three-d.service';
import { ViewboxOption } from 'src/app/models/viewboxOption.model';
import { TranslateService } from '@ngx-translate/core';
import { PriceService } from 'src/app/services/price.service';
import { StartupService } from 'src/app/services/startup.service';
import { ViewboxOptionWithPrice } from 'src/app/models/wallFinishType.model';
import { Configuration } from 'src/app/models/configuration.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ProductClassificationService } from 'src/app/services/product-classification.service';

@Component({
  selector: 'app-step-six',
  templateUrl: './step-six.component.html',
  styleUrls: ['./step-six.component.scss'],
})
export class StepSixComponent implements OnInit, OnDestroy {
  private _externalWallFinishOptions: ViewboxOptionWithPrice<FinishTypes>[];
  private _internalWallFinishOptions: ViewboxOptionWithPrice<FinishTypes>[];
  private unsubscribe: Subject<void> = new Subject();
  selectedWall: WallSide = null;
  selectedInnerWallId: string;
  pricesVisible: boolean;

  constructor(
    private configurationService: ConfigurationService,
    private threeDService: ThreeDService,
    private translateService: TranslateService,
    private priceService: PriceService,
    private startupService: StartupService,
    private changeDetector: ChangeDetectorRef,
    private productClassificationService: ProductClassificationService
  ) {
    this._externalWallFinishOptions = Object.values(this.configurationService.externalWallFinishViewboxOptions).filter(x => x.disabled === false);
    this._internalWallFinishOptions = Object.values(this.configurationService.internalWallFinishViewboxOptions).filter(x => x.disabled === false);
    this.productClassificationService.SetFinishClassification(this._externalWallFinishOptions);
    this.productClassificationService.SetFinishClassification(this._internalWallFinishOptions);
  }

  ngOnInit() {
    this.threeDService.setZoom(5);
    this.threeDService.resetDefaultCamera(true);

    this.updateSelectedViewboxOption();

    this.threeDService.wallSelectionEnabled = true;
    this.threeDService.ceilingSelectionEnabled = true;

    this.threeDService.wallSelectionChanged$.pipe(takeUntil(this.unsubscribe)).subscribe((side: WallSide) => {
      if (side > -1) {
        this.selectedWall = side;
        if (side !== WallSide.inner) {
          this.setCameraBasedOnInternalExternal(side);
          this.selectedInnerWallId = undefined;
        } else {
          this.selectedInnerWallId = this.threeDService.wallSelection.getSelectedWall().innerWall.id;
        }
      } else {
        this.selectedWall = null;
      }

      this.updatePricesForWallFinishes();
      this.updateSelectedViewboxOption();
    });

    this.startupService.priceEnabledSetting$.pipe(takeUntil(this.unsubscribe)).subscribe((enabled: boolean) => {
      this.pricesVisible = enabled;
    });

    this.updatePricesForWallFinishes();
  }

  ngOnDestroy() {
    this.threeDService.wallSelectionEnabled = false;
    this.threeDService.ceilingSelectionEnabled = false;

    this.unsubscribe.next();
    this.unsubscribe.unsubscribe();
  }

  updateSelectedViewboxOption() {
    this.externalWallFinishTypes.forEach(wfo => {
      wfo.selected = false;
    });
    this.internalWallFinishTypes.forEach(wfo => {
      wfo.selected = false;
    });
    if (this.selectedWall === null) {
      if (this.allWallsHaveTheSameFinish(true)) {
        const currentWallFinish = this.externalWallFinishTypes.find(
          ewf => ewf.option === this.configurationService.configuration.settings.wallFinishes[0].exterior
        );
        if (currentWallFinish !== undefined) {
          currentWallFinish.selected = true;
        }
      } else {
        this.externalWallFinishTypes.forEach(wfo => {
          wfo.selected = false;
        });
      }

      if (this.allWallsHaveTheSameFinish(false)) {
        const currentWallFinish = this.internalWallFinishTypes.find(
          ewf => ewf.option === this.configurationService.configuration.settings.wallFinishes[0].interior
        );

        if (currentWallFinish !== undefined) {
          currentWallFinish.selected = true;
        }
      } else {
        this.internalWallFinishTypes.forEach(wfo => {
          wfo.selected = false;
        });
      }
    } else {
      if (this.selectedWall !== WallSide.inner) {
        const currentExternalWallFinish = this.externalWallFinishTypes.find(
          ewf =>
            ewf.option === this.configurationService.configuration.settings.wallFinishes[this.selectedWall].exterior
        );

        if (currentExternalWallFinish !== undefined) {
          currentExternalWallFinish.selected = true;
        }

        const currentInternalWallFinish = this.internalWallFinishTypes.find(
          ewf =>
            ewf.option === this.configurationService.configuration.settings.wallFinishes[this.selectedWall].interior
        );

        if (currentInternalWallFinish !== undefined) {
          currentInternalWallFinish.selected = true;
        }
      } else {
        const selectedWall = this.threeDService.wallSelection.getSelectedWall();

        const currentExternalWallFinish = this.externalWallFinishTypes.find(
          ewf => ewf.option === selectedWall.innerWall.exteriorFinish
        );

        if (currentExternalWallFinish !== undefined) {
          currentExternalWallFinish.selected = true;
        }

        const currentInternalWallFinish = this.internalWallFinishTypes.find(
          ewf => ewf.option === selectedWall.innerWall.interiorFinish
        );
        if (currentInternalWallFinish !== undefined) {
          currentInternalWallFinish.selected = true;
        }
      }
    }

    this.changeDetector.detectChanges();
  }

  allWallsHaveTheSameFinish(exterior: boolean) {
    let equalFinish = true;
    let previousFinish: WallFinish = null;

    const currentExternalWallFinishes = this.getWallFinishesWithoutFloor();

    if (exterior) {
      for (const finish of currentExternalWallFinishes) {
        if (previousFinish !== null) {
          equalFinish = previousFinish.exterior === finish.exterior;
        }

        if (!equalFinish) {
          return equalFinish;
        }

        previousFinish = finish;
      }
    } else {
      for (const finish of currentExternalWallFinishes) {
        if (previousFinish !== null) {
          equalFinish = previousFinish.interior === finish.interior;
        }

        if (!equalFinish) {
          return equalFinish;
        }

        previousFinish = finish;
      }
    }

    return equalFinish;
  }

  getWallFinishesWithoutFloor(): WallFinish[] {
    return Object.values(this.configurationService.configuration.settings.wallFinishes)
      .slice(0, -1)
      .concat(
        this.configurationService.configuration.settings.innerWalls.map(
          iw => ({ exterior: iw.exteriorFinish, interior: iw.interiorFinish }) as WallFinish
        )
      );
  }

  setCameraBasedOnInternalExternal(side: WallSide) {
    if (!this.configurationService.wallsHidden) {
      this.threeDService.setCameraOnSide(side);
    } else {
      if (side > 1) {
        this.threeDService.setCameraOnSide(side - 2);
      } else {
        this.threeDService.setCameraOnSide(side + 2);
      }
    }
  }

  private updatePricesForWallFinishes() {
    this.internalWallFinishTypes.forEach(wallFinish => {
      wallFinish.priceChange = this.computePriceDifferenceForWallFinishes(wallFinish.option as FinishTypes, false);
    });

    this.externalWallFinishTypes.forEach(wallFinish => {
      wallFinish.priceChange = this.computePriceDifferenceForWallFinishes(wallFinish.option as FinishTypes, true);
    });
  }

  computePriceDifferenceForWallFinishes(newFinish: FinishTypes, outside: boolean): number | false {
    if (!this.pricesVisible) {
      return false;
    }

    const configuration = this.configurationService.configuration;
    let newConfig = configuration.clone();

    if (this.selectedWall === null) {
      newConfig = this.setupConfigForAllWalls(newFinish, outside, newConfig);
    } else {
      newConfig = this.setupConfig(newFinish, outside, this.selectedWall, newConfig, this.selectedInnerWallId);
    }

    const oldPrice = this.priceService.calculateTotalPriceExcludingDoors(configuration);
    const newPrice = this.priceService.calculateTotalPriceExcludingDoors(newConfig);
    if (oldPrice === false || newPrice === false) {
      return false;
    }
    return newPrice - oldPrice;
  }

  setupConfig(
    newFinish: FinishTypes,
    outside: boolean,
    wallSide: WallSide,
    newConfig: Configuration,
    innerWallid?: string
  ): Configuration {
    if (!innerWallid) {
      const doorsToUpdate = newConfig.settings.doorSettings.filter(d => d.side === wallSide);

      if (outside) {
        newConfig.settings.wallFinishes[wallSide].exterior = newFinish;
        doorsToUpdate.forEach(d => (d.frameExteriorFinish = newFinish));
      } else {
        newConfig.settings.wallFinishes[wallSide].interior = newFinish;
        doorsToUpdate.forEach(d => (d.frameInteriorFinish = newFinish));
      }
    } else {
      const doorsToUpdate = newConfig.settings.doorSettings.filter(
        d => d.side === wallSide && d.innerWallId == innerWallid
      );
      const innerWall = newConfig.settings.innerWalls.find(iw => iw.id == innerWallid);
      if (outside) {
        innerWall.exteriorFinish = newFinish;
        doorsToUpdate.forEach(d => (d.frameExteriorFinish = newFinish));
      } else {
        innerWall.interiorFinish = newFinish;
        doorsToUpdate.forEach(d => (d.frameInteriorFinish = newFinish));
      }
    }

    return newConfig;
  }

  setupConfigForAllWalls(newFinish: FinishTypes, outside: boolean, newConfig: Configuration): Configuration {
    newConfig = this.setupConfig(newFinish, outside, WallSide.north, newConfig);
    newConfig = this.setupConfig(newFinish, outside, WallSide.east, newConfig);
    newConfig = this.setupConfig(newFinish, outside, WallSide.south, newConfig);
    newConfig = this.setupConfig(newFinish, outside, WallSide.west, newConfig);
    if (!outside) {
      this.configurationService.configuration.settings.innerWalls.forEach(iw => {
        newConfig = this.setupConfig(newFinish, true, WallSide.inner, newConfig, iw.id);
        newConfig = this.setupConfig(newFinish, false, WallSide.inner, newConfig, iw.id);
      });
    }
    newConfig = this.setupConfig(newFinish, outside, WallSide.ceiling, newConfig);

    return newConfig;
  }

  get externalWallFinishTypes(): ViewboxOptionWithPrice<FinishTypes>[] {
    return this._externalWallFinishOptions;
  }

  get internalWallFinishTypes(): ViewboxOptionWithPrice<FinishTypes>[] {
    return this._internalWallFinishOptions;
  }

  get selectedWallCaption() {
    if (this.selectedWall !== null) {
      if (this.selectedWall === WallSide.inner) {
        if (this.selectedInnerWallId !== undefined) {
          return (
            (this.translateService.instant('WALL.' + this.selectedWall.toString()) as string).toLowerCase() +
            ' ' +
            this.getId(this.selectedInnerWallId)
          );
        }
      }
      return (this.translateService.instant('WALL.' + this.selectedWall.toString()) as string).toLowerCase();
    } else {
      return '';
    }
  }

  updateWallFinish(wallFinishUpdate: WallFinishUpdate) {
    if (wallFinishUpdate.wallSide === null) {
      this.configurationService.updateWallFinish(WallSide.east, wallFinishUpdate.exterior, wallFinishUpdate.finishType);
      this.configurationService.updateWallFinish(
        WallSide.north,
        wallFinishUpdate.exterior,
        wallFinishUpdate.finishType
      );
      this.configurationService.updateWallFinish(
        WallSide.south,
        wallFinishUpdate.exterior,
        wallFinishUpdate.finishType
      );
      this.configurationService.updateWallFinish(WallSide.west, wallFinishUpdate.exterior, wallFinishUpdate.finishType);
      this.configurationService.updateWallFinish(
        WallSide.ceiling,
        wallFinishUpdate.exterior,
        wallFinishUpdate.finishType
      );
      if (!wallFinishUpdate.exterior) {
        this.configurationService.configuration.settings.innerWalls.forEach(iw => {
          this.configurationService.updateWallFinish(WallSide.inner, false, wallFinishUpdate.finishType, iw.id);
          this.configurationService.updateWallFinish(WallSide.inner, true, wallFinishUpdate.finishType, iw.id);
        });
      }

      this.updatePricesForWallFinishes();
      return;
    }
    this.configurationService.updateWallFinish(
      wallFinishUpdate.wallSide,
      wallFinishUpdate.exterior,
      wallFinishUpdate.finishType,
      wallFinishUpdate.innerWallId
    );

    this.updatePricesForWallFinishes();
  }

  processSelectedExternalWallFinish(wallFinish: ViewboxOptionWithPrice<FinishTypes>) {
    this.handleSelectionState(this.externalWallFinishTypes, wallFinish);
    this.updateWallFinish(
      new WallFinishUpdate(this.selectedWall, wallFinish.option as FinishTypes, true, this.selectedInnerWallId)
    );
    this.configurationService.productClass = this.productClassificationService.GetFinishClassification(wallFinish.option);
  }

  processSelectedInternalWallFinish(wallFinish: ViewboxOptionWithPrice<FinishTypes>) {
    this.handleSelectionState(this.internalWallFinishTypes, wallFinish);
    this.updateWallFinish(
      new WallFinishUpdate(this.selectedWall, wallFinish.option as FinishTypes, false, this.selectedInnerWallId)
    );
    this.configurationService.productClass = this.productClassificationService.GetFinishClassification(wallFinish.option);
  }

  handleSelectionState(
    wallFinishOptions: ViewboxOption<FinishTypes>[],
    wallFinish: ViewboxOptionWithPrice<FinishTypes>
  ) {
    wallFinishOptions.forEach(finish => {
      finish.selected = false;
    });

    const wallFinishToUpdate = wallFinishOptions.find(f => f === wallFinish);
    if (wallFinishToUpdate) {
      wallFinishToUpdate.selected = true;
    }
  }
  getId(id: string) {
    if (!id) {
      return '';
    }
    const names = id.split('-');
    if (names.length > 1) {
      return +names[1] + 1;
    }
    return '';
  }
}
